import React from "react";
import { motion } from "framer-motion";

export default function TAELSAdaptiveSection() {
  const appleEase = [0.16, 1, 0.3, 1];

  return (
    <section className="bg-black text-white relative overflow-hidden border-t border-white/[0.08] min-h-screen flex flex-col justify-center py-20 md:py-28">
      {/* FOZZIL Signature Ambient Aura */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] bg-[radial-gradient(ellipse_70%_50%_at_50%_50%,rgba(6,93,192,0.12),transparent_70%)] blur-3xl pointer-events-none" />

      {/* Hero Header */}
      <motion.div
        initial={{ opacity: 0, y: 35 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.85, ease: appleEase }}
        className="max-w-6xl mx-auto px-6 pt-20 md:pt-28 pb-14 text-center relative z-10"
      >
        <span className="text-xs uppercase tracking-[0.25em] text-blue-400 font-mono font-semibold">
          Adaptive Cognition
        </span>
        <h2 className="text-3xl sm:text-5xl md:text-6xl font-bold tracking-tight text-white mt-3 mb-6">
          It doesn’t just answer.<br />It evolves with you.
        </h2>
        <p className="text-base sm:text-lg md:text-xl text-zinc-400 max-w-3xl mx-auto font-light leading-relaxed">
          Most AI models treat every session as day one. T.A.E.L.S. builds a living, privacy-preserved cognitive model of your thinking patterns, domain vocabulary, and operational rhythms—transforming from a generic assistant into your native intellectual twin.
        </p>
      </motion.div>

      {/* Bento Grid Layout (2 cards per row responsive on mobile with FOZZIL signature theme) */}
      <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 gap-3.5 sm:gap-8 pb-24 md:pb-32 relative z-10">
        
        {/* Card 1: Dynamic Adaptation */}
        <motion.div
          initial={{ opacity: 0, y: 35, scale: 0.98 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.75, ease: appleEase }}
          className="rounded-[22px] sm:rounded-[32px] bg-gradient-to-b from-white/[0.05] via-[#070b14] to-black border border-white/[0.08] hover:border-[#065DC0]/50 p-4 sm:p-8 md:p-10 flex flex-col justify-between relative overflow-hidden group transition-all duration-500 shadow-[0_15px_40px_rgba(0,0,0,0.7)] hover:shadow-[0_20px_60px_rgba(6,93,192,0.2)]"
        >
          {/* Ambient Corner Glow on Hover */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 blur-[80px] pointer-events-none rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

          <div className="relative z-10">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-blue-400 mb-4 sm:mb-6 font-mono text-lg sm:text-xl font-bold shadow-[0_0_20px_rgba(6,93,192,0.25)] group-hover:scale-105 group-hover:border-blue-400/50 transition-all">
              Δ
            </div>
            <h3 className="text-base sm:text-2xl font-semibold text-white mb-2 sm:mb-3 tracking-tight group-hover:text-blue-100 transition-colors">
              Fluid Domain Calibration
            </h3>
            <p className="text-xs sm:text-sm md:text-base text-zinc-400 leading-relaxed font-light">
              Whether you’re writing low-level systems architecture in Rust, structuring clinical trial meta-analyses, or drafting strategic venture documents, T.A.E.L.S. automatically shifts its mental models, depth of rigor, and specialized terminology to match your exact context.
            </p>
          </div>

          <div className="mt-6 sm:mt-10 pt-4 sm:pt-6 border-t border-white/[0.08] flex flex-col sm:flex-row sm:items-center justify-between text-[10px] sm:text-xs font-mono text-zinc-500 gap-1.5 sm:gap-0 relative z-10">
            <span>CONTEXT WINDOW: 2M+ TOKENS</span>
            <span className="text-blue-400 font-semibold tracking-wider">ZERO LATENCY SHIFT</span>
          </div>
        </motion.div>

        {/* Card 2: Continuous Self-Learning */}
        <motion.div
          initial={{ opacity: 0, y: 35, scale: 0.98 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.75, delay: 0.1, ease: appleEase }}
          className="rounded-[22px] sm:rounded-[32px] bg-gradient-to-b from-white/[0.05] via-[#070b14] to-black border border-white/[0.08] hover:border-sky-500/50 p-4 sm:p-8 md:p-10 flex flex-col justify-between relative overflow-hidden group transition-all duration-500 shadow-[0_15px_40px_rgba(0,0,0,0.7)] hover:shadow-[0_20px_60px_rgba(56,189,248,0.2)]"
        >
          {/* Ambient Corner Glow on Hover */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-sky-500/10 blur-[80px] pointer-events-none rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

          <div className="relative z-10">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-sky-500/10 border border-sky-500/30 flex items-center justify-center text-sky-400 mb-4 sm:mb-6 font-mono text-lg sm:text-xl font-bold shadow-[0_0_20px_rgba(56,189,248,0.25)] group-hover:scale-105 group-hover:border-sky-400/50 transition-all">
              ⚡
            </div>
            <h3 className="text-base sm:text-2xl font-semibold text-white mb-2 sm:mb-3 tracking-tight group-hover:text-sky-100 transition-colors">
              Autonomous Self-Refinement
            </h3>
            <p className="text-xs sm:text-sm md:text-base text-zinc-400 leading-relaxed font-light">
              Through continuous in-session synthesis and secure persistent memory vectors, T.A.E.L.S. learns your preferences, formatting styles, and logical constraints on the fly—drastically reducing repetitive prompting and compounding your daily velocity.
            </p>
          </div>

          <div className="mt-6 sm:mt-10 pt-4 sm:pt-6 border-t border-white/[0.08] flex flex-col sm:flex-row sm:items-center justify-between text-[10px] sm:text-xs font-mono text-zinc-500 gap-1.5 sm:gap-0 relative z-10">
            <span>IN-SESSION OPTIMIZATION</span>
            <span className="text-sky-400 font-semibold tracking-wider">PRIVACY ENCLAVE SECURED</span>
          </div>
        </motion.div>

      </div>
    </section>
  );
}
